
import React, { useState } from 'react';
export interface PaymentFormProps {
  cardNumber: string;
  handleCardNumberChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  cardNumberError: string;
  validateCardNumber: () => void;
  cardTypeLogo: string;
  cardType: string;
  expiryDate: string;
  handleExpiryDateChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  expiryDateError: string;
  validateExpiryDate: () => void;
  cvv: string;
  handleCvvChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  cvvError: string;
  validateCvv: () => void;
  maxLength: number;
  name: string;
  setName: (value: string) => void;
  nameError: string;
  validateName: () => void;
}

import { 
  Box, 
  TextField, 
  Stack, 
  InputAdornment, 
  Typography,
  Paper,
  IconButton,
  Tooltip,
  useTheme,
  Collapse
} from '@mui/material';
import { 
  CreditCard,
  CalendarMonth,
  Person,
  LockOutlined,
  InfoOutlined,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';

const PaymentDetailsForm: React.FC<PaymentFormProps> = ({
  cardNumber,
  handleCardNumberChange,
  cardNumberError,
  validateCardNumber,
  cardTypeLogo,
  cardType,
  expiryDate,
  handleExpiryDateChange,
  expiryDateError,
  validateExpiryDate,
  cvv,
  handleCvvChange,
  cvvError,
  validateCvv,
  maxLength,
  name,
  setName,
  nameError,
  validateName
}) => {
  const theme = useTheme();
  const [showCVV, setShowCVV] = useState(false);

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p:{xs:0, sm:3},
        border: {xs:"none", sm:'1px solid'},
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper'
      }}
    >
      <Typography 
        variant="h6" 
        gutterBottom 
        sx={{ 
          mb: 3, 
          display: 'flex', 
          alignItems: 'center',
          gap: 1,
          color: 'primary.main',
          fontWeight: 600
        }}
      >
        <CreditCard /> Payment Details
      </Typography>

      <Box sx={{ mb: 3 }}>
        <TextField
          label="Card Number"
          variant="outlined"
          fullWidth
          value={cardNumber}
          onChange={handleCardNumberChange}
          error={!!cardNumberError}
          helperText={cardNumberError}
          inputProps={{ maxLength: 19 }}
          onBlur={validateCardNumber}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <CreditCard color="action" />
              </InputAdornment>
            ),
            endAdornment: cardType ? (
              <InputAdornment position="end">
                <Box
                  component="img"
                  src={cardTypeLogo}
                  alt={cardType}
                  sx={{ 
                    width: 40, 
                    height: 'auto',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'scale(1.1)'
                    }
                  }}
                />
              </InputAdornment>
            ) : null,
            sx: {
              '& input': {
                letterSpacing: '1.5px',
                fontFamily: 'monospace'
              }
            }
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                  borderWidth: 2
                }
              }
            }
          }}
        />
      </Box>

      <Stack 
        direction={{ xs: 'column', sm: 'row' }} 
        spacing={2} 
        sx={{ mb: 3 }}
      >
        <TextField
          label="Expiry Date"
          variant="outlined"
          fullWidth
          value={expiryDate}
          onChange={handleExpiryDateChange}
          error={!!expiryDateError}
          helperText={expiryDateError}
          inputProps={{ 
            maxLength: 5,
            placeholder: 'MM/YY'
          }}
          onBlur={validateExpiryDate}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <CalendarMonth color="action" />
              </InputAdornment>
            )
          }}
        />
        <TextField
          label="CVV"
          variant="outlined"
          fullWidth
          value={cvv}
          onChange={handleCvvChange}
          error={!!cvvError}
          helperText={cvvError}
          onBlur={validateCvv}
          inputProps={{ 
            maxLength, 
            type: showCVV ? 'text' : 'password'
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlined color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title="CVV is the 3 or 4 digit security code on the back of your card">
                  <IconButton
                    size="small"
                    onClick={() => setShowCVV(!showCVV)}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    {showCVV ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            )
          }}
        />
      </Stack>

      <Box sx={{ mb: 2 }}>
        <TextField
          label="Cardholder Name"
          variant="outlined"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={!!nameError}
          helperText={nameError}
          onBlur={validateName}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Person color="action" />
              </InputAdornment>
            )
          }}
          placeholder="Name as shown on card"
        />
      </Box>

      <Collapse in={!!cardNumberError || !!expiryDateError || !!cvvError || !!nameError}>
        <Paper 
          elevation={0} 
          sx={{ 
            mt: 2,
            p: 2,
            bgcolor: 'error.light',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <InfoOutlined color="error" />
          <Typography variant="body2" color="error.dark">
            Please correct the errors above to proceed with the payment.
          </Typography>
        </Paper>
      </Collapse>
    </Paper>
  );
};

export default PaymentDetailsForm;